<template>
    <page-container>
        <campaign-list-header :withoutOverview="props.withoutOverview" :count="totalCount"></campaign-list-header>
        <campaign-controls v-if="totalCount > 0 || !filtersAreDefault"
            v-model="fetchParams"
            @reset="resetFilters" @apply="applyFilters" :groupId="groupId" :orgUnitId="orgUnitId"
        ></campaign-controls>

        <page-callout class="my-12" section="form-submission" shadow/>

        <template v-if="!loading && !list.length">
            <empty-state v-if="totalCount === 0 && !filtersAreDefault" @reset="resetFilters" />
            <empty-state v-else-if="totalCount === 0">
                No campaigns yet.
                <router-button v-if="isCorporate" :to="{ name: 'campaigns.new' }">
                    New Campaign
                    <font-awesome-icon widthAuto :icon="faCirclePlus"/>
                </router-button>
            </empty-state>
        </template>

        <div v-if="loading && !list.length" class="text-center py-10">
            <vx-icon-loading class="w-9 h-9 animate-loading-spin"></vx-icon-loading>
        </div>

        <vx-table
            v-if="list.length"
            :class="[isCorporate ? 'grid-cols-1 sm:grid-cols-2 gap-4 lg:grid-cols-1' : xlGridClass]"
            rowBreakpoint="xl"
        >
            <vx-table-row v-if="!isCorporate" isHeader data-test="header-row">
                <div>
                    Name
                </div>
                <div>
                    Status
                </div>
                <div>
                    Contacts
                </div>
                <div v-if="showCostColumn">
                    Cost
                </div>
                <div>
                    Send on
                </div>
            </vx-table-row>

            <template v-if="isCorporate">
                <campaign-card v-for="(campaign, index) in list" :key="campaign.id" v-model="list[index]"
                    :contactsCount="corporateCounts.get(campaign.id)"
                    @deleted="removeFromList($event)"
                    @launch="modalCampaignIndex = index;">
                </campaign-card>
            </template>
            <template v-else>
                <campaign-row v-for="(campaign, index) in list" :key="campaign.id" v-model="list[index]"
                    :readOnly="props.readOnly"
                    :marketingAllowed="marketingAllowed"
                    :transactionalAllowed="transactionalAllowed"
                    :showCostColumn="showCostColumn"
                    :metrics="campaignMetrics.get(campaign.id)"
                    @deleted="removeFromList($event)">
                </campaign-row>
            </template>

        </vx-table>

        <div v-if="!loading && list.length" class="mt-8 flex justify-between items-center w-full gap-10">
            <vx-button v-if="fetchParams.cursor" @click="loadData()" color="muted-dark" size="xl"
                data-test="load-more-btn"
                class="grow sm:grow-0">
                Load More
                <font-awesome-icon widthAuto :icon="faSpinner" :spin="loading"></font-awesome-icon>
            </vx-button>

            <div class="text-lg grow-0 ml-auto font-normal text-slate-600">
                {{ list.length }}/{{ totalCount }}
            </div>
        </div>

        <campaign-launch-modal
            v-if="isCorporate"
            :visible="!!list[modalCampaignIndex]"
            @update:visible="modalCampaignIndex = undefined"
            v-model:campaign="list[modalCampaignIndex]"
        />
    </page-container>
</template>

<script setup>
import PageContainer from '~/components/general/PageContainer.vue';
import EmptyState from '~/components/general/EmptyState.vue';
import CampaignListHeader from './CampaignListHeader.vue';
import CampaignControls from './CampaignControls.vue';
import { faCirclePlus, faSpinner } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxButton, VxTable, VxTableRow, VxIconLoading } from '@voxie/frontend-components';
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import CampaignCard from '../campaign-card/CampaignCard.vue';
import { campaignsClient } from '~/services';
import { useCostVisibility } from '~/composables/useCostVisibility';
import outboundMessagesAnalyticsClient from '~/services/analytics/outboundMessages/outboundMessagesAnalyticsClient';
import CampaignLaunchModal from '../CampaignLaunchModal.vue';
import { chunkArray } from '~/utils/array';
import { processWithConcurrency } from '~/utils/async';
import dayjs from '~/utils/dayjs';
import { useToasted } from '~/composables/useToasted';
import { useCursorPagination } from '~/composables/useCursorPagination';
import CampaignRow from '../campaign-card/CampaignRow.vue';
import PageCallout from '~/components/general/PageCallout.vue';
import { useCurrentGroup } from '~/composables/useCurrentGroup';
import RouterButton from '~/components/general/RouterButton.vue';
import { useCallout } from '~/composables/useCallout';
import { useFilterPersistence } from '~/composables/useFilterPersistence';
import { useMessageRules } from '~/components/quick-blast/useMessageRules';
import { BILLING_CUTOFF_DATE } from '~/utils/analytics';

const props = defineProps({
    orgUnitId: {
        type: Number,
    },
    groupId: {
        type: String,
    },
    readOnly: {
        type: Boolean,
        default: false,
    },
    withoutOverview: {
        type: Boolean,
        default: false,
    },
});

const teamId = Spark.state.currentTeam.id;

const toasted = useToasted();

const callout = useCallout();

const { currentOrgUnit, currentGroup } = useCurrentGroup();
const orgUnitId = computed(() => props.orgUnitId || currentOrgUnit.value?.id);
const groupId = computed(() => props.groupId || currentGroup.value?.group_id);

const { getMessageRules, marketingAllowed, transactionalAllowed } = useMessageRules();

const totalCount = ref(0);
const { list, loading: dataLoading, fetchParams, fetchRecords } = useCursorPagination();

const modalCampaignIndex = ref();

const isCorporate = computed(() => !groupId.value && !orgUnitId.value);

const { showEstimatedCost } = useCostVisibility();

const campaignMetrics = ref(new Map());
const corporateCounts = ref(new Map());

const loading = dataLoading;

const MAX_CONCURRENT_REQUESTS = 4;

const showCostColumn = computed(() => !isCorporate.value && showEstimatedCost.value);

const xlGridClass = computed(() => {
    if (!props.readOnly && showCostColumn.value) {
        return 'xl:grid-cols-[minmax(0,1fr)_repeat(5,max-content)]';
    }

    if (props.readOnly && !showCostColumn.value) {
        return 'xl:grid-cols-[minmax(0,1fr)_repeat(3,max-content)]';
    }

    return 'xl:grid-cols-[minmax(0,1fr)_repeat(4,max-content)]';
});

const getCampaignDataSource = (campaign) => {
    const { status, started_at, completed_at } = campaign;
    const now = dayjs.utc();

    if (['draft', 'scheduled', 'published'].includes(status)) {
        return 'estimation';
    }

    if (status === 'completed' && completed_at) {
        const completedTime = dayjs.utc(completed_at);
        if (completedTime.isAfter(now.subtract(2, 'hours'))) {
            return 'estimation';
        }

        if (started_at && dayjs.utc(started_at).isSameOrAfter(BILLING_CUTOFF_DATE)) {
            return 'metrics';
        }
    }

    return 'legacy';
};

const fetchEstimations = async (campaigns) => {
    if (campaigns.length === 0) {
        return;
    }

    const batches = chunkArray(campaigns, 10);

    for (const batch of batches) {
        const batchIds = batch.map((c) => c.id);

        try {
            let response;
            if (orgUnitId.value) {
                response = await campaignsClient.orgUnitCampaignsEstimations(teamId, orgUnitId.value, batchIds);
            } else if (groupId.value) {
                response = await campaignsClient.groupCampaignsEstimations(teamId, groupId.value, batchIds);
            }

            if (response?.data?.items) {
                response.data.items.forEach((item) => {
                    const contactsCount = item.audience_size;
                    const estimatedCost = contactsCount > 0 && item.estimated_cost > 0 ? item.estimated_cost : null;
                    campaignMetrics.value.set(item.campaign_id, { contactsCount, estimatedCost });
                });
            }
        } catch (e) {
            console.error(e);
            batchIds.forEach((id) => {
                campaignMetrics.value.set(id, {
                    contactsCount: null,
                    estimatedCost: null,
                });
            });
        }
    }
};

const fetchMetricsForCampaign = async (campaign) => {
    try {
        const startHour = dayjs.utc(campaign.started_at).startOf('hour');
        const endHour = dayjs.utc(campaign.completed_at).add(1, 'hour').startOf('hour');

        const context = {
            teamId,
            orgUnitId: orgUnitId.value,
            groupId: groupId.value,
        };

        const params = {
            range: [startHour.format('YYYY-MM-DDTHH:mm:ss[Z]'), endHour.format('YYYY-MM-DDTHH:mm:ss[Z]')],
            filters: {
                resources: [{ resource_id: campaign.id, resource_type: 'one_time_campaign' }],
            },
        };

        const response = await outboundMessagesAnalyticsClient.getExportResourceMetrics(context, params);
        const metrics = response?.data?.data?.find((item) => item.resource_id === campaign.id);

        const contactsCount = metrics?.total_messages_sent ?? 0;
        const messagingCost = metrics?.messaging_cost ?? 0;
        const estimatedCost = contactsCount > 0 && messagingCost > 0 ? messagingCost : null;

        campaignMetrics.value.set(campaign.id, { contactsCount, estimatedCost});
    } catch (e) {
        console.error(e);
        campaignMetrics.value.set(campaign.id, {
            contactsCount: null,
            estimatedCost: null,
        });
    }
};

const loadMetricsForCampaigns = (campaigns) => {
    const campaignsWithEstimatedData = campaigns.filter((c) => getCampaignDataSource(c) === 'estimation');
    const campaignsWithContactsOnly = campaigns.filter((c) => getCampaignDataSource(c) === 'metrics');
    const campaignsWithoutMetrics = campaigns.filter((c) => getCampaignDataSource(c) === 'legacy');

    campaignsWithoutMetrics.forEach((c) => {
        campaignMetrics.value.set(c.id, {
            contactsCount: null,
            estimatedCost: null,
        });
    });

    fetchEstimations(campaignsWithEstimatedData);
    processWithConcurrency(campaignsWithContactsOnly, MAX_CONCURRENT_REQUESTS, fetchMetricsForCampaign);
};

const fetchCorporateContactsCount = async (campaign) => {
    try {
        const response = await campaignsClient.campaignsContactsCount(teamId, campaign.id);
        corporateCounts.value.set(campaign.id, response.data.count);
    } catch (e) {
        console.error(e);
        corporateCounts.value.set(campaign.id, null);
    }
};

const loadCorporateCounts = (campaigns) => {
    processWithConcurrency(campaigns, MAX_CONCURRENT_REQUESTS, fetchCorporateContactsCount);
};

const defaultFilters = () => ({
    query: '',
    per_page: 10,
    cursor: null,
    duration_types: [],
    ui_statuses: [],
    start_date: undefined,
    end_date: undefined,
    time_zone_aware: undefined,
    category: undefined,
    sort_field: 'id',
    sort_dir: 'desc',
});

const { isDefault: filtersAreDefault } = useFilterPersistence('campaigns-list-filters', fetchParams, defaultFilters);

const resetFilters = () => {
    fetchParams.value = defaultFilters();
    loadData();
};

const applyFilters = () => {
    fetchParams.value.cursor = undefined;
    loadData();
};

onMounted(() => {
    loadData();

    if (!isCorporate.value) {
        getMessageRules({ orgUnitId: orgUnitId.value, groupId: groupId.value });
    }
});

watch(
    list,
    (newList) => {
        if (isCorporate.value) {
            loadCorporateCounts(newList.filter((c) => !corporateCounts.value.has(c.id)));
        } else {
            loadMetricsForCampaigns(newList.filter((c) => !campaignMetrics.value.has(c.id)));
        }
    },
    { immediate: true }
);

const fetchTotalCount = async () => {
    try {
        let request;

        if (isCorporate.value) {
            request = campaignsClient.campaignsCount(teamId, { ...fetchParams.value });
        } else if (orgUnitId.value) {
            request = campaignsClient.orgUnitCampaignsCount(teamId, orgUnitId.value, { ...fetchParams.value });
        } else {
            request = campaignsClient.groupCampaignsCount(teamId, groupId.value, { ...fetchParams.value });
        }

        const response = await request;
        totalCount.value = response.data?.count || 0;
    } catch (e) {
        console.error(e);
        toasted.global.platform_error();
    }
};

const removeFromList = (campaignId) => {
    list.value = list.value.filter((item) => item.id !== campaignId);
    totalCount.value = totalCount.value - 1;

    if (!list.value.length) {
        loadData();
    }
};

const loadData = () => {
    let request;

    if (isCorporate.value) {
        request = campaignsClient.campaignsGetRecords(teamId, { ...fetchParams.value });
    } else if (orgUnitId.value) {
        request = campaignsClient.orgUnitCampaignsGetRecords(teamId, orgUnitId.value, { ...fetchParams.value });
    } else {
        request = campaignsClient.groupCampaignsGetRecords(teamId, groupId.value, { ...fetchParams.value });
    }

    fetchRecords(request);
    fetchTotalCount();
};

onUnmounted(() => {
    callout.clear('form-submission');
});
</script>
